import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, FlaskConical, TrendingUp, Clock, AlertTriangle,
  CheckCircle2, Users, Bell, Globe, Send, Languages, ThumbsUp, ThumbsDown, Mail,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
          PieChart, Pie, Cell } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { litlabAgentAnalytics, litlabAgentSlaCheck, litlabAgentWeeklyDigestPreview, litlabAgentWeeklyDigestSend } from "@/lib/api";

const RANGES = [
  { key: 7, label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];

const LANG = {
  en: { label: "English", color: "#4f46e5", flag: "EN" },
  es: { label: "Español", color: "#059669", flag: "ES" },
  fr: { label: "Français", color: "#d97706", flag: "FR" },
};

export default function AdminIrisAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [slaBusy, setSlaBusy] = useState(false);
  const [slaFlash, setSlaFlash] = useState(null);

  const load = () => {
    setLoading(true); setErr("");
    litlabAgentAnalytics(days)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Not authorized. Sign in as admin."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    document.title = "Iris Analytics — Admin";
    load();
  }, [days]); // eslint-disable-line

  const runSlaCheck = async () => {
    setSlaBusy(true); setSlaFlash(null);
    try {
      const r = await litlabAgentSlaCheck();
      setSlaFlash({ ok: true, msg: r.escalated ? `Escalated ${r.escalated} lapsed quote(s).` : "No lapsed quotes right now." });
      load();
    } catch (e) {
      setSlaFlash({ ok: false, msg: e?.response?.data?.detail || "SLA check failed" });
    } finally { setSlaBusy(false); }
  };

  const totals = data?.totals || { total: 0, opened: 0, unopened: 0, escalated: 0 };
  const langMix = data?.language_mix || { en: 0, es: 0, fr: 0 };
  const langPieData = Object.entries(langMix)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: LANG[k]?.label || k, value: v, color: LANG[k]?.color || "#64748b" }));
  const topInd = data?.top_industries || [];
  const timeseries = data?.timeseries || [];
  const recent = data?.recent || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <Link to="/admin/assistants-analytics" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="iris-analytics-back">
          <ArrowLeft className="w-4 h-4" /> Back to AI assistants
        </Link>
        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-sky-700 font-bold border border-sky-200 bg-sky-50 rounded-full px-3 py-1">
              <FlaskConical className="w-3.5 h-3.5" /> Admin · Iris (L.I.T. Labs)
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900" data-testid="iris-analytics-title">
              Iris analytics
            </h1>
            <p className="mt-2 text-slate-600 text-sm max-w-2xl">
              Every quote Iris submits, over the last {days} days.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setDays(r.key)}
                data-testid={`iris-analytics-range-${r.key}`}
                className={`text-xs uppercase font-bold px-3 py-1.5 rounded-full border transition ${
                  days === r.key
                    ? "bg-sky-600 text-white border-sky-700"
                    : "bg-white text-slate-600 border-slate-300 hover:border-sky-400"
                }`}>
                {r.label}
              </button>
            ))}
            <button onClick={runSlaCheck} disabled={slaBusy} data-testid="iris-sla-check"
              className="text-xs uppercase font-bold px-3 py-1.5 rounded-full border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50 flex items-center gap-1">
              {slaBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
              Escalate lapsed
            </button>
          </div>
        </div>

        {slaFlash && (
          <div className={`mt-4 rounded-lg p-3 text-sm flex items-center gap-2 ${
            slaFlash.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"
          }`} data-testid="iris-sla-flash">
            {slaFlash.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {slaFlash.msg}
          </div>
        )}

        {err && <div className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="iris-analytics-error">{err}</div>}
        {loading && !data && (
          <div className="mt-12 text-slate-500 flex items-center gap-2 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        )}

        {data && (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Tile label="Quote submissions" value={totals.total} icon={FlaskConical} tone="indigo" tid="iris-total-quotes" />
              <Tile label="Opened by metallurgist" value={totals.opened} icon={CheckCircle2} tone="emerald" tid="iris-opened" />
              <Tile label="Unopened / waiting" value={totals.unopened} icon={Users} tone="slate" tid="iris-unopened" />
              <Tile label="Escalated (SLA lapsed)" value={totals.escalated} icon={AlertTriangle} tone={totals.escalated > 0 ? "rose" : "slate"} tid="iris-escalated" />
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4" data-testid="iris-avg-response">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 border border-emerald-200 grid place-items-center">
                  <Clock className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Avg time to open</div>
                  <div className="text-2xl font-bold text-slate-900">{data.avg_response_human ?? "—"}</div>
                  <div className="text-[11px] text-slate-500">{totals.opened} opened / {totals.total} total</div>
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4" data-testid="iris-conversion">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 border border-indigo-200 grid place-items-center">
                  <TrendingUp className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Open rate</div>
                  <div className="text-2xl font-bold text-slate-900">
                    {totals.total ? `${Math.round(totals.opened / totals.total * 100)}%` : "—"}
                  </div>
                  <div className="text-[11px] text-slate-500">of Iris quote submissions</div>
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4" data-testid="iris-lang-tile">
                <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 grid place-items-center">
                  <Languages className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Languages served</div>
                  <div className="text-2xl font-bold text-slate-900">{langPieData.length || 0}</div>
                  <div className="text-[11px] text-slate-500">
                    {Object.entries(langMix).filter(([, v]) => v > 0).map(([k, v]) => `${LANG[k]?.flag || k} ${v}`).join(" · ")}
                  </div>
                </div>
              </div>
              <CsatTile csat={data.csat} />
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6" data-testid="iris-timeseries">
                <div className="text-sm font-bold text-slate-900">Daily quote volume</div>
                <div className="mt-4 h-64">
                  {timeseries.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={timeseries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                        <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="quotes" fill="#0284c7" radius={[4, 4, 0, 0]} name="Quotes" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full grid place-items-center text-slate-400 text-sm">No data yet</div>
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="iris-language-pie">
                <div className="text-sm font-bold text-slate-900">Language mix</div>
                <div className="mt-4 h-64">
                  {langPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={langPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                          {langPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full grid place-items-center text-slate-400 text-sm">No data yet</div>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 justify-center text-xs">
                  {langPieData.map((l) => (
                    <span key={l.name} className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                      {l.name} · {l.value}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {topInd.length > 0 && (
              <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6" data-testid="iris-industries">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <Globe className="w-4 h-4 text-slate-500" /> Top industries
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {topInd.map((ind, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-3">
                      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{ind.industry}</div>
                      <div className="text-2xl font-bold text-slate-900 mt-1">{ind.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recent.length > 0 && (
              <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6" data-testid="iris-recent">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <Send className="w-4 h-4 text-slate-500" /> Recent Iris quotes
                </div>
                <div className="mt-4 divide-y divide-slate-100">
                  {recent.map((r) => (
                    <RecentRow key={r.id} q={r} />
                  ))}
                </div>
              </div>
            )}

            <DigestPanel />
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}


function CsatTile({ csat }) {
  const c = csat || { up: 0, down: 0, total: 0, score_pct: null };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4" data-testid="iris-csat">
      <div className="w-12 h-12 rounded-lg bg-emerald-50 border border-emerald-200 grid place-items-center">
        <ThumbsUp className="w-6 h-6 text-emerald-600" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">CSAT</div>
        <div className="text-2xl font-bold text-slate-900">{c.score_pct != null ? `${c.score_pct}%` : "—"}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2">
          <span className="inline-flex items-center gap-0.5"><ThumbsUp className="w-2.5 h-2.5 text-emerald-500" /> {c.up}</span>
          <span className="inline-flex items-center gap-0.5"><ThumbsDown className="w-2.5 h-2.5 text-rose-500" /> {c.down}</span>
        </div>
      </div>
    </div>
  );
}


function DigestPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    litlabAgentWeeklyDigestPreview()
      .then(setPreview)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const send = async (force) => {
    setSending(true); setFlash(null);
    try {
      const r = await litlabAgentWeeklyDigestSend(force);
      if (r.sent) setFlash({ ok: true, msg: `Digest sent. ${(r.channels || []).join(" · ")}` });
      else setFlash({ ok: false, msg: r.reason || "Digest send failed" });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || "Digest send failed" });
    } finally { setSending(false); }
  };

  return (
    <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6" data-testid="digest-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-200 grid place-items-center">
            <Mail className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900" data-testid="digest-title">Weekly digest</div>
            <div className="text-xs text-slate-500">Auto-sent every Monday when admins view analytics. Configurable recipients come from the AI Assistants alerts panel.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => send(false)} disabled={sending} data-testid="digest-send"
            className="text-xs uppercase font-bold px-3 py-1.5 rounded-full border border-slate-300 hover:border-indigo-400 flex items-center gap-1 disabled:opacity-50">
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send now
          </button>
          <button onClick={() => send(true)} disabled={sending} data-testid="digest-send-force"
            className="text-xs uppercase font-bold px-3 py-1.5 rounded-full border border-indigo-300 text-indigo-800 hover:bg-indigo-50 flex items-center gap-1 disabled:opacity-50">
            Force resend
          </button>
        </div>
      </div>

      {flash && (
        <div className={`mt-3 rounded-lg p-3 text-sm flex items-center gap-2 ${
          flash.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"
        }`} data-testid="digest-flash">
          {flash.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {flash.msg}
        </div>
      )}

      {loading && <div className="mt-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading preview…</div>}
      {preview && (
        <div className="mt-4 rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="digest-preview"
          dangerouslySetInnerHTML={{ __html: preview.html }} />
      )}
    </div>
  );
}


function Tile({ label, value, icon: Icon, tone, tid }) {
  const bg = { indigo: "#eef2ff", emerald: "#ecfdf5", amber: "#fffbeb", slate: "#f8fafc", rose: "#fef2f2" }[tone] || "#f8fafc";
  const fg = { indigo: "#4f46e5", emerald: "#059669", amber: "#d97706", slate: "#475569", rose: "#e11d48" }[tone] || "#475569";
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: bg }}>
          <Icon className="w-4 h-4" style={{ color: fg }} />
        </div>
      </div>
      <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
    </div>
  );
}


function RecentRow({ q }) {
  const status = q.escalated_at
    ? { label: "Escalated", tone: "rose" }
    : q.opened_at
      ? { label: "Opened", tone: "emerald" }
      : { label: "Waiting", tone: "slate" };
  const bg = { emerald: "#ecfdf5", rose: "#fef2f2", slate: "#f1f5f9" }[status.tone];
  const fg = { emerald: "#065f46", rose: "#9f1239", slate: "#475569" }[status.tone];
  const langMeta = LANG[q.language] || { flag: (q.language || "?").toUpperCase() };
  return (
    <div className="py-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-mono text-[11px] text-slate-500">#{q.reference}</span>
          <span className="font-semibold text-slate-900">{q.name}</span>
          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: langMeta.color || "#64748b" }} />
            {langMeta.flag}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-slate-500 truncate">
          {q.industry || "—"} · created {new Date(q.created_at).toLocaleString()}
        </div>
      </div>
      <span className="text-[11px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1"
        style={{ background: bg, color: fg }}>
        {status.label}
      </span>
    </div>
  );
}
